import { Component, Injectable, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OpeningSystemType } from 'src/app/enum/closet-doors-custom/opening-system-type.enum';
import { OpeningSystem } from 'src/app/models/configurators-2d/applications/closet-doors-custom/opening-system';
import { OpeningSystemSide } from 'src/app/models/configurators-2d/applications/closet-doors-custom/opening-system-side';
import { ClosetDoorsCustomService } from 'src/app/services/configurators-2d/applications/closet-doors-custom.service';
import { distinct } from 'src/app/services/helpers/utils';
import { ClosetDoorsCustomConfiguratorItem } from '../../../configurator/applications/closet-doors-custom-configurator-item';
import { IBaseComponent } from '../../../configurator/i-base-component';
import { Sizes } from './../../../../../models/configurators-2d/applications/closet-doors-custom/sizes';

@Component({
  selector: 'ng-closet-doors-custom-closet-configuration',
  templateUrl: './closet-configuration.component.html',
})
@Injectable({ providedIn: 'root' })
export class ClosetDoorsCustomClosetConfigurationComponent
  extends ClosetDoorsCustomConfiguratorItem
  implements OnInit, IBaseComponent
{
  readonly OpeningSystemType = OpeningSystemType;

  openingSystems: OpeningSystem[];
  heightLimits: Sizes;
  widthLimits: Sizes;

  numberOfPanel: number[] = new Array();

  currentLang: string;
  baseImgURL: string;

  constructor(
    private translate: TranslateService,
    closetDoorsCustomService: ClosetDoorsCustomService
  ) {
    super(closetDoorsCustomService);
    this.baseImgURL = this.service.apiUrl;
    this.currentLang = this.translate.getBrowserLang();
  }

  ngOnInit(): void {
    this.service.getHeightLimits().then(heightLimits => {
      this.heightLimits = heightLimits;
      this.heightChanged(true);
    });
  }

  heightChanged(init?: boolean) {
    if (init !== true) {
      this.reset();
      this.widthLimits = undefined;
    }

    if (
      this.configuration.clientHeight >= this.heightLimits.min &&
      this.configuration.clientHeight <= this.heightLimits.max
    ) {
      this.service.getWidthLimits().then(widthLimits => {
        this.widthLimits = widthLimits;
        if (this.configuration.clientWidth) {
          this.widthChanged(init);
        }
      });
    }
  }

  widthChanged(init?: boolean) {
    if (init !== true) {
      this.reset();
    }

    if (this.areDimensionsValid()) {
      this.service.getOpeningSystems().then(openingSystems => {
        this.openingSystems = openingSystems;

        if (init === true) {
          this.openingSystemInit();
        }
      });
    }
  }

  openingSystemInit() {
    if (this.configuration.openingSystem) {
      this.selectOpeningSystem(this.configuration.openingSystem);
    }

    if (this.configuration.configurationDoorStructures?.length > 0) {
      this.configuration.nbDoor = this.configuration.configurationDoorStructures.length;
    }
  }

  reset() {
    this.openingSystems = undefined;
    this.configuration.openingSystem = undefined;
    this.configuration.openingSystemSide = undefined;
    this.configuration.percentageOpening = undefined;
    this.configuration.nbDoor = undefined;
    this.configuration.supplier = undefined;
    this.configuration.doorRange = undefined;
    this.configuration.doorStructure = undefined;
    this.configuration.configurationDoorStructures = undefined;
    this.configuration.frame = undefined;
    this.configuration.frameFinish = undefined;
    this.configuration.configurationAccessories = undefined;
    this.configuration.resume = undefined;
  }

  /**
   * Détermination si les dimensions sont valides
   * @returns boolean
   */
  areDimensionsValid(): boolean {
    if (this.heightLimits && this.widthLimits) {
      return (
        this.configuration.clientHeight >= this.heightLimits.min &&
        this.configuration.clientHeight <= this.heightLimits.max &&
        this.configuration.clientWidth >= this.widthLimits.min &&
        this.configuration.clientWidth <= this.widthLimits.max
      );
    }

    return false;
  }

  updateBaseboard() {
    if (this.configuration.clientHasBaseboard) {
      //valeur avant click
      this.configuration.baseboardQty = undefined;
      this.configuration.baseboardThickness = undefined;
    }
  }

  /**
   * Récupération des systèmes d'ouverture
   * @returns système d'ouverture
   */
  getOpeningSystemsAvailable(): OpeningSystem[] {
    if (this.openingSystems) {
      return this.openingSystems.filter(
        o => o.minWidth <= this.configuration.clientWidth && o.maxWidth >= this.configuration.clientWidth
      );
    }

    return [];
  }

  /**
   * Sélection des systèmes d'ouverture
   * @param opening système d'ouverture
   */
  selectOpeningSystem(opening: OpeningSystem) {
    this.numberOfPanel = new Array();

    //Reset des objets si le opening system change
    if (this.configuration.openingSystem?.id !== opening.id) {
      this.configuration.percentageOpening = null;
      this.configuration.openingSystemSide = null;
      this.configuration.nbDoor = undefined;
      this.configuration.supplier = undefined;
      this.configuration.doorRange = undefined;
      this.configuration.doorStructure = undefined;
      this.configuration.configurationDoorStructures = undefined;
      this.configuration.frame = undefined;
      this.configuration.frameFinish = undefined;
      this.configuration.configurationAccessories = undefined;
      this.configuration.resume = undefined;
    }

    this.configuration.openingSystem = opening;
    for (let index = opening.minDoor; index <= opening.maxDoor; index++) {
      this.numberOfPanel.push(index);
    }
  }

  getOpeningSystemSidesNbDoor(): number[] {
    if (this.configuration.openingSystem) {
      return distinct(this.configuration.openingSystem.openingSystemSides.map(s => s.nbDoor));
    }

    return [];
  }

  /**
   * Mise à jour des systèmes d'ouverture
   * @param percentage pourcentage d'ouverture
   * @param nbDoor nombre de vantaux
   * @param nbWay
   */
  updateOpeningSystemPercentageAndPanel(percentage: number, nbDoor: number, nbWay: number): void {
    // si on a un changement de sélection du système d'ouverture, on reset les portes
    if (
      this.configuration.percentageOpening !== percentage ||
      this.configuration.nbWay !== nbWay ||
      this.configuration.nbDoor !== nbDoor
    ) {
      this.configuration.configurationDoorStructures = [];
    }

    this.configuration.percentageOpening = percentage;
    this.configuration.nbWay = nbWay || 1;
    this.configuration.nbDoor = nbDoor;
  }

  /**
   * Détermination si le système d'ouverture est sélectionné
   * @param id id du système d'ouverture
   * @returns boolean
   */
  isOpeningSelected(id: number): boolean {
    return this.configuration.openingSystem?.id == id;
  }

  /**
   * Sélection du côté de l'ouverture
   * @param openingSide côté d'ouverture
   */
  selectOpeningSide(openingSide: OpeningSystemSide): void {
    //Reset des objets si le opening side change
    if (this.configuration.openingSystemSide?.id !== openingSide.id) {
      this.configuration.supplier = undefined;
      this.configuration.doorRange = undefined;
      this.configuration.doorStructure = undefined;
      this.configuration.configurationDoorStructures = undefined;
      this.configuration.frame = undefined;
      this.configuration.frameFinish = undefined;
      this.configuration.configurationAccessories = undefined;
      this.configuration.resume = undefined;
    }

    this.configuration.openingSystemSide = openingSide;
  }

  /**
   * Détermination si le côté d'ouverture est sélectionné
   * @param id id du côté d'ouverture
   * @returns boolean
   */
  isOpeningSideSelected(id: number): boolean {
    return this.configuration.openingSystemSide?.id == id;
  }

  /**
   * Récupération des côtés d'ouverture disponibles
   * @param nbDoor nombre de vantaux
   * @returns tableau de côtés d'ouverture
   */
  availableOpeningSide(nbDoor: number): OpeningSystemSide[] {
    return this.configuration.openingSystem.openingSystemSides.filter(x => x.nbDoor == nbDoor);
  }

  isNextButtonActive(): boolean {
    let clientHasSize =
      this.configuration.clientHeight != 0 &&
      this.configuration.clientHeight != null &&
      this.configuration.clientWidth != 0 &&
      this.configuration.clientWidth != null;
    let clientHasOpening = this.configuration.openingSystem != null;
    let clientHasPanels = this.configuration.nbDoor != null && this.configuration.nbDoor != 0;
    let openingPercentOrOpeningSystemSide =
      this.configuration.openingSystem?.type == OpeningSystemType.sliding
        ? this.configuration.percentageOpening != null && this.configuration.percentageOpening != 0
        : this.configuration.openingSystemSide != null;
    let baseboardValid =
      this.configuration.openingSystem?.type != OpeningSystemType.sliding ||
      !this.configuration.clientHasBaseboard ||
      this.configuration.baseboardQty === 1 ||
      this.configuration.baseboardQty === 2;

    return clientHasSize && clientHasOpening && clientHasPanels && openingPercentOrOpeningSystemSide && baseboardValid;
  }

  checkPageChangeCapability(): boolean {
    return this.configuration.location != undefined;
  }
}
